import type { ReactNode } from "react";
import { toast as sonnerToast } from "sonner";

const GENERIC_ERROR_MESSAGE =
	"Something went wrong and we have recorded this incident. We're working to fix it, so please try again later.";

/**
 * Toast notification utilities.
 *
 * Drop-in replacement for Evergreen UI's `toaster`:
 *
 *   // Before (Evergreen):
 *   import { toaster } from "evergreen-ui";
 *   toaster.success("Saved");
 *
 *   // After:
 *   import { toast } from "@jasperlabs/ui";
 *   toast.success("Saved");
 *
 * For advanced usage (promises, custom JSX, dismiss by ID, etc.)
 * import `sonnerToast` directly.
 */
const toast = {
	success: (message: ReactNode, description?: string) =>
		sonnerToast.success(message, { description }),
	warning: (message: ReactNode, description?: string) =>
		sonnerToast.warning(message, { description }),
	error: (message: ReactNode, description?: string) =>
		sonnerToast.error(message, { description }),
	info: (message: ReactNode, description?: string) =>
		sonnerToast.info(message, { description }),
	notify: (message: ReactNode, description?: string) =>
		sonnerToast.info(message, { description }),
	/**
	 * Displays the canonical generic-error toast as a **warning** (amber, not red):
	 * "Something went wrong and we have recorded this incident. We're working to fix it,
	 * so please try again later."
	 */
	errorGeneric: () => sonnerToast.warning(GENERIC_ERROR_MESSAGE),
	dismiss: sonnerToast.dismiss,
};

export { toast, sonnerToast };
